import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';

import http from '@common/http';

import { Header, Container, Footer } from '@common/components';

import { Banner, Bookings, BookingSummary, BookingExpired, ColivingUpgrade } from './components';

import { HeaderMessages, Progress } from '@components/Coliving';

import { Grid, Typography } from '@material-ui/core';

import { useTranslation, toast, useDetails, useSessionState, useRecords } from '@common/utils';

import { keyBy, uniq } from 'lodash';

import { useSettings } from '@common/hooks';

function ColivingBookings({ history, match }) {
	const classes = useStyles();
	const { id } = match.params;
	const [state, setState] = useState(match.params);
	const [filter] = useState({});
	const [params, setParams] = useState('');
	const { setState: setBookingId } = useSessionState('booking-id', '');
	const { isUpgradesEnabled } = useSettings();
	const tr = useTranslation();

	const { details, fetchDetails } = useDetails(`/api/kiosks/coliving/bookings/${state.id}`, {
		createdAt: '',
		expiresAt: '',
		products: [],
		productsGrouped: [],
		total: 0,
		totalBeds: 0,
		totalFees: 0,
		totalProducts: 0,
		totalRooms: 0,
		totalServices: 0,
		totalMandatory: 0,
		updatedAt: '',
		_id: '',
		orders: [],
	}, [state]);

	const { records: upgrades } = useRecords('/api/kiosks/coliving/products/upgrades', filter, [id]);

	const removeReservations = async (reservations) => {
		const ids = details.products.filter(p => reservations.indexOf(p.reservation) !== -1).map(p => p._id);

		for (const bookingProductId of ids) {
			await http.delete(`/api/kiosks/coliving/bookings/${state.id}/products/${bookingProductId}`).catch(toast);
		}

		toast({ text: tr('Reservation removed successfully') });

		if (ids.length === details.products.filter(p => !p.productIsCityTaxApplied && !p.mandatory && !p.upgrade).length) {
			setBookingId('');
			history.replace('/');
		} else {
			setState({
				...state,
				updated: new Date().getTime(),
			});
		}
	};

	const [productById, setProductById] = useState({});

	const removeUpgrades = async (bookingProductIds = []) => {
		if (!bookingProductIds?.length) {
			return;
		}

		for (const bookingProductId of bookingProductIds) {
			await http.delete(`/api/kiosks/coliving/bookings/${state.id}/products/${bookingProductId}`).catch(toast);
		}

		setState({
			...state,
			updated: new Date().getTime(),
		});
	};

	const handleAddUpgrade = async ({ id, quantity = 1 }) => {
		await http
			.post(`/api/kiosks/coliving/bookings/${details._id}/products/upgrades`, { product: id, quantity })
			.then(() => fetchDetails())
			.catch(toast);
	};

	const handleRemoveUpgrade = async ({ id }) => removeUpgrades([id]);

	const bookings = useMemo(() => details.productsGrouped.filter(p => !p.fee && !p.mandatory), [details.productsGrouped]);

	useEffect(() => {

		Promise
			.all(uniq(details.products, 'product').map(p => http.get(`/api/kiosks/coliving/products/${p.product}`).catch(() => ({}))))
			.then(responses => responses.map(response => response.data).filter(Boolean))
			.then(products => setProductById(keyBy(products, '_id')))
			.catch(error => {
				console.error(error);
				toast(error);
			});

		const products = details.productsGrouped.filter(p => !p.fee && !p.mandatory && !p.upgrade);
		if (products.length) {
			const { from, to } = products[products?.length - 1];
			setParams(`?from=${from}&to=${to}`);
		}

	}, [details.productsGrouped]);

	return (
		<div className={classes.root}>
			<HeaderMessages />
			<Header>
				<Progress status="booking" id={id} />
			</Header>

			<Container>
				{isUpgradesEnabled &&
					<Grid container spacing={4} style={{ paddingBottom: '100px' }} >
						<Grid item xs={12}>
							<Typography variant="h1">
								{tr('Extras and upgrades')}
							</Typography>
						</Grid>
						<Grid
							item
							container
							xs={12} md={8}
							alignContent='flex-start'
							className={classes.container}
						>
							{upgrades.map((upgrade, i) => {
								return (
									<Grid item key={i} xs={12} md={6} className={classes.upgrades}>
										<ColivingUpgrade
											booking={details}
											upgrade={upgrade}
											handleAddUpgrade={handleAddUpgrade}
											handleRemoveUpgrade={handleRemoveUpgrade}
										/>
									</Grid>
								);
							})}
						</Grid>
						<Grid item xs={12} md={4}>
							{details && !details.expired &&
								<BookingSummary
									bookingId={id}
									total={details.total}
									totalFees={details.totalFees}
									totalBeds={details.totalBeds}
									totalRooms={details.totalRooms}
									totalServices={details.totalServices}
									totalMandatory={details.totalMandatory}
									totalUpgrades={details.totalUpgrades}
									productsGrouped={details?.productsGrouped || []}
									productById={productById}
									isInitial={details.products.every(p => !p.order)}
									removeReservations={removeReservations}
								/>
							}
							{details && details.expired &&
								<BookingExpired />
							}
							{(!details || !details.expired) &&
								<Banner params={params} />
							}
						</Grid>
					</Grid>
				}
				{!isUpgradesEnabled &&
					<Grid container spacing={3} style={{ paddingBottom: '100px' }}>
						<Grid container spacing={2} style={{ paddingBottom: '24px' }}>
							<Grid item xs={12}>
								<Typography variant="h1">
									{tr('Your booking details')}
								</Typography>
							</Grid>
						</Grid>

						{details &&
							<Bookings bookings={bookings} removeReservations={removeReservations} />
						}

						<Grid item xs={12} md={4} style={{ paddingTop: '4px' }}>
							{details && !details.expired &&
								<BookingSummary
									bookingId={id}
									total={details.total}
									totalFees={details.totalFees}
									totalBeds={details.totalBeds}
									totalRooms={details.totalRooms}
									totalServices={details.totalServices}
									totalMandatory={details.totalMandatory}
									productsGrouped={details?.productsGrouped || []}
									productById={productById}
									isInitial={details.products.every(p => !p.order)}
								/>
							}
							{details && details.expired &&
								<BookingExpired />
							}
							{(!details || !details.expired) &&
								<Banner params={params} />
							}
						</Grid>

						{/*
					<Grid container spacing={6}>
						<Grid item xs={12}>
							<Typography variant="h1">
								{tr('Add these services to enjoy your work and stay')}
							</Typography>
						</Grid>
					</Grid>

					<Services services={services} />
				*/}
					</Grid>
				}
			</Container>
			<Footer />
		</div>
	);
}

ColivingBookings.propTypes = {
	history: PropTypes.object,
	match: PropTypes.object,
};

export default ColivingBookings;

const useStyles = makeStyles(() => ({
	root: {
		flexGrow: 1,
		position: 'relative',
		minHeight: '96vh',
	},
	container: {
		display: 'flex',
	},
	upgrades: {
		display: 'flex',
		flex: '1 1 auto',
	},
}));

